/**
 * 保单录入表单（**受控组件** · B 域 B5 · 依据 B 域 PRD §5.5.5 字段表）
 *
 * ## 🔴 为什么是"受控 + 内联 Card"，而不是自带状态 + Dialog
 *
 * | 选择 | 理由 |
 * |---|---|
 * | **受控**（值在外面） | 表单与"草稿→请求"的映射（`features/policy-draft.ts`）分开 ⇒ 后者可用**零依赖基座**直接断言，不必渲染 |
 * | 🔴 **内联 Card（不用 Dialog）** | MUI `Dialog` 走 `Portal`，在 `renderToStaticMarkup` 下内容是否进产物**取决于实现细节** ⇒ 渲染断言会变得不可靠。本项目端侧测试基座是"静态渲染"（见 `test/render.tsx`），**能用普通容器就不要用 Portal** |
 *
 * ## 字段与"谁有权判定"
 *
 * 本组件**不做任何校验、不做任何金额换算** —— 它只负责"把人的输入搬进 `draft`"：
 * - 必填与金额边界的判定在 `buildPolicyRequest()`（其中**语义规则**来自共享层内核 `checkPolicyShape`）；
 * - 错误清单由调用方以 `errors` 传入，本组件只**展示**（服务端的 `message` 也是原样展示）。
 *
 * ## 🔴 被保人为什么只有一个输入框
 *
 * B 域 PRD §5.5.5 原文：「P0 即建关联表 `policy_insureds`，**UI 首版只露 1 个被保人**；
 * 「多被保人」编辑入口 **P1** 打开」。⇒ 契约与被保人表**都是 1:N**，但 P0 界面只放 1 位，
 * 提交时包装成 1 元素数组（见 `policy-draft.ts`）。P1 打开时只改本组件与草稿的**这一处**。
 *
 * ⚠️ **已知边界（已登记）**：P0 提交是"整体替换被保人集合"⇒ 编辑一张多被保人保单会把它
 * 收敛成 1 位。P0 画不出多被保人保单（界面只让填 1 位），故当前**不可达**；
 * P1 打开多被保人编辑时**必须一并处理**（见交接文档 §6 登记项）。
 */

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { currencySymbolOf } from '@jiaying/shared';
import type { ChangeEvent } from 'react';
import { POLICY_STATUS_OPTIONS } from '../features/policy-status.js';
import type { PolicyDraft } from '../features/policy-draft.js';

export interface PolicyFormProps {
  draft: PolicyDraft;
  /** 任一字段变化 —— 由调用方合并（本组件不持有状态） */
  onChange: (patch: Partial<PolicyDraft>) => void;
  /** 本地或服务端的问题清单（**原样展示**，不重写文案） */
  errors: readonly string[];
  /** 提交中（禁用按钮，防重复提交） */
  busy: boolean;
  mode: 'create' | 'edit';
  /** 账本本币 —— 🔴 用于金额字段标签（`DR-036` D1：金额必带币种） */
  currency: string;
  onSubmit: () => void;
  onCancel: () => void;
}

export function PolicyForm({
  draft,
  onChange,
  errors,
  busy,
  mode,
  currency,
  onSubmit,
  onCancel,
}: PolicyFormProps) {
  const sym = currencySymbolOf(currency);
  const set = (key: keyof PolicyDraft) => (e: ChangeEvent<HTMLInputElement>) => {
    onChange({ [key]: e.target.value } as Partial<PolicyDraft>);
  };

  return (
    <Card data-testid="policy-form">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {mode === 'create' ? '新增保单' : '编辑保单'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          保单号在本账本内唯一；保额不计净资产，现金价值计入（仅生效中 / 已缴清）
        </Typography>

        {errors.length > 0 && (
          <Alert severity="error" sx={{ mt: 1.5 }} data-testid="policy-form-errors">
            <Stack component="ul" sx={{ m: 0, pl: 2 }}>
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </Stack>
          </Alert>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={1.5}>
          {/* ── ① 标识 ── */}
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' } }}>
            <TextField
              label="保单号"
              required
              value={draft.policyNo}
              onChange={set('policyNo')}
              inputProps={{ 'data-testid': 'policyNo' }}
            />
            <TextField
              label="保险公司"
              required
              value={draft.company}
              onChange={set('company')}
              inputProps={{ 'data-testid': 'company' }}
              helperText="保单台账按公司名分组"
            />
          </Box>

          <TextField
            label="险种"
            required
            value={draft.insuranceType}
            onChange={set('insuranceType')}
            inputProps={{ 'data-testid': 'insuranceType' }}
            helperText="重疾 / 医疗 / 年金 / 寿险 / 意外……（自由填写，未做枚举）"
          />

          {/* ── ② 三人分离（PRD §5.5.5 硬要求）── */}
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr 1fr' } }}>
            <TextField
              label="投保人"
              required
              value={draft.policyholder}
              onChange={set('policyholder')}
              inputProps={{ 'data-testid': 'policyholder' }}
            />
            <TextField
              label="被保人"
              required
              value={draft.insured}
              onChange={set('insured')}
              inputProps={{ 'data-testid': 'insured' }}
              helperText="P0 只填 1 位；多被保人编辑在 P1 打开"
            />
            <TextField
              label="受益人"
              required
              value={draft.beneficiary}
              onChange={set('beneficiary')}
              inputProps={{ 'data-testid': 'beneficiary' }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            三人可不同；可填家庭成员姓名，或系统用户 id（<code>usr_…</code>）—— 服务端按「id 优先、姓名兜底」解析
          </Typography>

          {/* ── ③ 保险期间 ── */}
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' } }}>
            <TextField
              label="保险期间起"
              required
              type="date"
              value={draft.startDate}
              onChange={set('startDate')}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'startDate' }}
            />
            <TextField
              label="保险期间止"
              type="date"
              value={draft.endDate}
              onChange={set('endDate')}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'endDate' }}
              helperText="长期险可留空"
            />
          </Box>

          {/* ── ④ 金额（币种来自账本本币，见文件头）── */}
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr 1fr' } }}>
            <TextField
              label={`保额（${sym}）`}
              required
              value={draft.sumAssuredText}
              onChange={set('sumAssuredText')}
              inputProps={{ 'data-testid': 'sumAssuredText', inputMode: 'decimal' }}
              helperText="不计净资产"
            />
            <TextField
              label={`现金价值（${sym}）`}
              required
              value={draft.cashValueText}
              onChange={set('cashValueText')}
              inputProps={{ 'data-testid': 'cashValueText', inputMode: 'decimal' }}
              helperText="计入净资产"
            />
            <TextField
              label={`年缴保费（${sym}）`}
              required
              value={draft.annualPremiumText}
              onChange={set('annualPremiumText')}
              inputProps={{ 'data-testid': 'annualPremiumText', inputMode: 'decimal' }}
            />
          </Box>

          {/* ── ⑤ 缴费与状态 ── */}
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' } }}>
            <TextField
              label="缴费方式"
              required
              value={draft.paymentMode}
              onChange={set('paymentMode')}
              inputProps={{ 'data-testid': 'paymentMode' }}
              helperText="如 趸交 / 年交 / 月交（自由填写，未做枚举）"
            />
            <TextField
              select
              label="承保状态"
              value={draft.status}
              onChange={set('status')}
              inputProps={{ 'data-testid': 'status' }}
              helperText="决定现金价值是否计入净资产（服务端判定）"
            >
              {POLICY_STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button onClick={onCancel} disabled={busy} data-testid="policy-cancel">
            取消
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={busy}
            data-testid="policy-submit"
          >
            {busy ? '提交中…' : mode === 'create' ? '新增' : '保存'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
